// MCRO001-F0030 / T-MCRO-030 — resumable private video ingest, browser side.
// Design: docs/design/MCRO001-D007-resumable-video-ingest.md (studioaeye-choreo).
// These calls are same-origin at /video-ingest/uploads/{uploadId}, NOT under
// config.apiBaseUrl (/api) and NOT a choreography URL per api.js's
// isChoreographyRequestUrl — they must present the Core-minted uploadToken as
// an explicit Bearer, never the Choreo session subject token. window.fetch
// (not the axios `api` instance) keeps that boundary structurally obvious, the
// same pattern choreographyAuth.js uses for same-origin /oauth/* calls.

const CHUNK_BYTES = 8 * 1024 * 1024; // service-enforced ceiling (D007 §2.1)
const POLL_INITIAL_MS = 800;
const POLL_MAX_MS = 5000;
const POLL_BACKOFF = 1.6;
// finalize() in rht/studioaeye-video-ingest src/ingest-service.js synchronously
// awaits the full ffmpeg transcode (processor.process) before its HTTP response
// is sent (server.js's finalize route awaits service.finalize(...) end to end).
// For a real video that exceeds the reverse-proxy read timeout and the browser
// sees a 502/gateway/network failure — but the service has already persisted
// lifecycle 'processing' to its durable session store (store.save) BEFORE
// starting the transcode, and nothing in finalize() is wired to the HTTP
// request/response lifecycle (no AbortSignal, no response.on('close')
// listener) — so the transcode keeps running server-side after the client's
// connection drops, and GET /video-ingest/uploads/{id} keeps reading the same
// durable store independent of that dropped connection. A finalize transport
// failure is therefore NOT evidence the ingest failed; it only means the
// client can no longer see the synchronous response, so it must fall through
// to the SAME lifecycle poll the happy path uses (D007 lifecycle: GET status
// is the source of truth) rather than surface a fatal error.
const POLL_CEILING_MS = 30 * 60 * 1000; // generous ceiling for a long real-video transcode

// Structured service failure codes that mean "definitely not done, but not a
// reason to fail fast either" — the ingest service can return these
// synchronously (e.g. the processing gate is saturated) yet the upload is
// still retained and progressing; treat them the same as a transport-level
// finalize failure and keep polling instead of surfacing them as terminal.
const FINALIZE_TRANSIENT_CODES = new Set(['VIDEO_INGEST_PROCESSING_BUSY', 'VIDEO_INGEST_UNAVAILABLE',
  'VIDEO_INGEST_STORAGE_UNAVAILABLE', 'VIDEO_INGEST_STORAGE_EXHAUSTED']);
const TRANSIENT_HTTP_STATUS = new Set([0, 408, 502, 503, 504]);

// True when a finalize() failure carries no definitive terminal signal from
// the ingest service itself — i.e. it is a network/timeout/gateway failure
// (or a body the service didn't produce, such as a proxy's HTML timeout
// page) rather than a structured VIDEO_INGEST_* validation/processing
// failure. Only the latter is safe to fail fast on; everything else must
// fall through to the lifecycle poll per D007 (GET status is authoritative).
export function isFinalizeTransient(error) {
  if (!error) return false;
  if (error.name === 'TypeError' || error.status === undefined) return true; // fetch network failure/abort
  if (TRANSIENT_HTTP_STATUS.has(error.status)) return true;
  if (!error.code || error.code === 'VIDEO_INGEST_ERROR') return true; // no structured body — not the service talking
  return FINALIZE_TRANSIENT_CODES.has(error.code);
}

export function videoIngestUploadPath(uploadId) {
  return `/video-ingest/uploads/${encodeURIComponent(uploadId)}`;
}

function ingestError(body, status) {
  const code = body?.error?.code || 'VIDEO_INGEST_ERROR';
  const message = body?.error?.message || 'The video upload service returned an error.';
  const error = new Error(message);
  error.code = code;
  error.status = status;
  error.retryable = Boolean(body?.error?.retryable);
  return error;
}

async function parseJson(response) {
  try { return await response.json(); } catch { return {}; }
}

async function digestSha256Base64(bytes) {
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  let binary = '';
  new Uint8Array(digest).forEach((byte) => { binary += String.fromCharCode(byte); });
  return window.btoa(binary);
}

// One 8 MiB-max chunk, offset-bound and checksummed per D007 §2.1. Returns the
// service's authoritative lifecycle/offset payload (`data`); throws on any
// non-2xx with the safe error code (401 VIDEO_INGEST_TOKEN_INVALID on expiry).
export async function patchChunk(uploadId, uploadToken, offset, bytes) {
  const checksum = await digestSha256Base64(bytes);
  const response = await window.fetch(videoIngestUploadPath(uploadId), {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      Authorization: `Bearer ${uploadToken}`,
      'Content-Type': 'application/offset+octet-stream',
      'Upload-Offset': String(offset),
      'Upload-Checksum': `sha256 ${checksum}`,
    },
    body: bytes,
  });
  const body = await parseJson(response);
  if (!response.ok || body.success !== true) throw ingestError(body, response.status);
  return body.data;
}

export async function finalizeUpload(uploadId, uploadToken) {
  const response = await window.fetch(`${videoIngestUploadPath(uploadId)}/finalize`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Authorization: `Bearer ${uploadToken}` },
  });
  const body = await parseJson(response);
  if (!response.ok || body.success !== true) throw ingestError(body, response.status);
  return body.data;
}

export async function getUploadStatus(uploadId, uploadToken) {
  const response = await window.fetch(videoIngestUploadPath(uploadId), {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Authorization: `Bearer ${uploadToken}` },
  });
  const body = await parseJson(response);
  if (!response.ok || body.success !== true) throw ingestError(body, response.status);
  return body.data;
}

// Sequential chunk loop, resuming from the service's authoritative offset
// (never trusts a locally-tracked offset — D007 requires resume-from-offset
// after loss/replay). onProgress receives 0..1 for the "uploading" phase.
export async function uploadChunks(file, uploadId, uploadToken, startOffset, onProgress) {
  let offset = startOffset;
  while (offset < file.size) {
    const end = Math.min(offset + CHUNK_BYTES, file.size);
    const bytes = await file.slice(offset, end).arrayBuffer();
    const status = await patchChunk(uploadId, uploadToken, offset, bytes);
    offset = status.uploadOffset;
    onProgress?.(file.size > 0 ? offset / file.size : 1);
  }
  return offset;
}

// Polls GET lifecycle with capped exponential backoff until a terminal state
// (ready/failed/cancelled/expired). Never retries forever: terminal failure
// states resolve immediately with the safe failure payload for the caller to
// surface, per D007's "no silent retry-forever" requirement. A generous
// overall ceiling (default 30 min, matching a long real-video transcode)
// stops the poll with a clear, non-crashing timeout error rather than hanging
// indefinitely — the upload keeps processing server-side regardless; the
// caller may re-poll later using the same uploadId/uploadToken.
export async function pollUntilTerminal(
  uploadId,
  uploadToken,
  onPhase,
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  { ceilingMs = POLL_CEILING_MS, now = () => Date.now() } = {},
) {
  let delay = POLL_INITIAL_MS;
  const deadline = now() + ceilingMs;
  for (;;) {
    const status = await getUploadStatus(uploadId, uploadToken);
    onPhase?.(status);
    if (status.lifecycle === 'ready') return status;
    if (['failed', 'cancelled', 'expired'].includes(status.lifecycle)) {
      const error = new Error(status.failure?.message || 'The video upload did not complete.');
      error.code = status.failure?.code || 'VIDEO_INGEST_FAILED';
      error.retryable = Boolean(status.failure?.retryable);
      error.status = status;
      throw error;
    }
    if (now() >= deadline) {
      const error = new Error('The video is still processing, but this took longer than expected. '
        + 'It will keep processing; check back shortly and it should appear in the library.');
      error.code = 'VIDEO_INGEST_POLL_TIMEOUT';
      error.retryable = true;
      error.status = status;
      throw error;
    }
    await sleep(delay);
    delay = Math.min(delay * POLL_BACKOFF, POLL_MAX_MS);
  }
}

// MCRO001-D007: finalize() may not respond before the reverse-proxy read
// timeout for a long real transcode (see isFinalizeTransient's citation of
// rht/studioaeye-video-ingest finalize()/server.js above). A transient
// finalize failure (network error, 502/503/504/408, or an unparseable body)
// is therefore not evidence of failure — fall through to the same lifecycle
// poll the fast-finalize happy path already uses, so the client reaches the
// correct terminal outcome (ready -> proceed; failed/cancelled/expired ->
// surface the real error) instead of a false-negative "upload failed".
// Only a structured, definitive VIDEO_INGEST_* failure from finalize itself
// (e.g. a validation 4xx) fails fast without polling.
export async function finalizeThenAwaitReady(uploadId, uploadToken, onPhase, sleep, pollOptions) {
  try {
    const finalizeStatus = await finalizeUpload(uploadId, uploadToken);
    if (finalizeStatus.lifecycle === 'ready' || finalizeStatus.lifecycle === 'processing' || finalizeStatus.lifecycle === 'validating') {
      onPhase?.(finalizeStatus);
      if (finalizeStatus.lifecycle === 'ready') return finalizeStatus;
    }
  } catch (error) {
    if (!isFinalizeTransient(error)) throw error;
    onPhase?.({ lifecycle: 'processing' });
  }
  return pollUntilTerminal(uploadId, uploadToken, onPhase, sleep, pollOptions);
}
